import { integrationRepository, IntegrationRepository } from './IntegrationRepository'
import type {
  IntegrationActionResult,
  IntegrationCategory,
  IntegrationPatch,
  IntegrationRecord,
  IntegrationStatus,
} from './IntegrationTypes'

/**
 * Integration Center service — CRUD + placeholder connection actions.
 * Real login / providers arrive in future sprints.
 */
export class IntegrationService {
  private readonly repository: IntegrationRepository

  constructor(repository: IntegrationRepository = integrationRepository) {
    this.repository = repository
  }

  list(): IntegrationRecord[] {
    return this.repository.list()
  }

  get(id: string): IntegrationRecord | null {
    return this.repository.findById(id)
  }

  listByCategory(categoria: IntegrationCategory): IntegrationRecord[] {
    return this.list().filter((item) => item.categoria === categoria)
  }

  listByStatus(status: IntegrationStatus): IntegrationRecord[] {
    return this.list().filter((item) => item.status === status)
  }

  create(input: Omit<IntegrationRecord, 'id'> & { id?: string }): IntegrationRecord {
    const record: IntegrationRecord = {
      ...emptyCredentials(),
      ...input,
      id: input.id ?? `int-${Date.now().toString(36)}`,
      headers: input.headers ?? {},
      ultimaConexao: input.ultimaConexao ?? null,
      ultimoErro: input.ultimoErro ?? null,
    }
    return this.repository.upsert(record)
  }

  update(id: string, patch: IntegrationPatch): IntegrationRecord | null {
    const current = this.repository.findById(id)
    if (!current) return null
    const next: IntegrationRecord = {
      ...current,
      ...patch,
      id: current.id,
      headers: patch.headers ?? current.headers,
    }
    return this.repository.upsert(next)
  }

  /** Placeholder — no real network login in Sprint 2.2. */
  test(id: string): IntegrationActionResult {
    return placeholder(id, 'test', 'Teste de conexão preparado. Login ainda não implementado.')
  }

  /** Placeholder — marks status as Conectando only in memory message; no auth. */
  connect(id: string): IntegrationActionResult {
    const current = this.repository.findById(id)
    if (!current) {
      return {
        ok: false,
        message: 'Integração não encontrada.',
        placeholder: true,
        integrationId: id,
        action: 'connect',
      }
    }
    this.repository.upsert({
      ...current,
      status: 'Conectando',
      ultimoErro: null,
    })
    return placeholder(id, 'connect', 'Conectar preparado. Autenticação real em sprint futuro.')
  }

  /** Placeholder — sets Desativado / Nunca conectado style state without remote logout. */
  disconnect(id: string): IntegrationActionResult {
    const current = this.repository.findById(id)
    if (!current) {
      return {
        ok: false,
        message: 'Integração não encontrada.',
        placeholder: true,
        integrationId: id,
        action: 'disconnect',
      }
    }
    this.repository.upsert({
      ...current,
      status: 'Desativado',
      ativo: false,
    })
    return placeholder(id, 'disconnect', 'Desconectar preparado. Sem sessão remota neste sprint.')
  }

  /** Placeholder — logs UI hook. */
  logs(id: string): IntegrationActionResult {
    return placeholder(id, 'logs', 'Visualização de logs preparada. Sem provider ainda.')
  }

  /** Placeholder — edit dialog hook. */
  edit(id: string): IntegrationActionResult {
    return placeholder(id, 'edit', 'Edição preparada. Formulário completo em sprint futuro.')
  }
}

function placeholder(
  integrationId: string,
  action: IntegrationActionResult['action'],
  message: string,
): IntegrationActionResult {
  return { ok: true, message, placeholder: true, integrationId, action }
}

function emptyCredentials(): Pick<
  IntegrationRecord,
  'url' | 'usuario' | 'senha' | 'apiKey' | 'token' | 'cookies' | 'headers'
> {
  return {
    url: '',
    usuario: '',
    senha: '',
    apiKey: '',
    token: '',
    cookies: '',
    headers: {},
  }
}

export const integrationService = new IntegrationService()
